import React, { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';
import { StylableComponentProps } from '@clds/common-definitions';
import { globalTheme, combineValues } from '@clds/component-theme';
import { media } from '@clds/style-utils';
import { ColumnWrapper, Root as ColumnSectionRoot } from '../PanelSection/PanelSection.styled';
import { theme as t } from '../ColumnPanelTheme';
import { CheckboxRecord } from '../CheckboxRecord/CheckboxRecord';
import { PanelSection } from '../PanelSection/PanelSection';

type SizeOnly = Pick<ColumnPanelProps, 'size'>;
type RootProps = Required<SizeOnly>;

const getShadow = () => combineValues(t.boxShadow.color, globalTheme.palette.contrastInvert, (color, contrastColor) => mix(0.14, color, contrastColor));

type ThemeValue = (typeof t.height)[keyof typeof t.height];

const getHeightCss = (heightValue: ThemeValue) => css`
  height: ${heightValue};
  min-height: ${heightValue};
`;

const Root = styled.div<RootProps>`
  width: 100%;
  box-shadow: ${t.boxShadow.size} ${getShadow()};
  background-color: ${t.background.color};
  border-bottom: 1px solid ${t.border.color};

  @media ${media.tablet({ minOnly: true })} {
    display: flex;
    ${({ size }) => getHeightCss(t.height[size])};
    overflow: auto hidden;
    ${ColumnWrapper} {
      height: calc(100% - (${globalTheme.spacing.lg} + ${globalTheme.font.size.sm}));
    }
  }

  ${ColumnSectionRoot} {
    height: 100%;
    @media ${media.tablet({ minOnly: true })} {
      :not(:last-child) {
        border-right: 1px solid ${t.border.color};
      }
    }

    @media ${media.phone()} {
      height: initial;
      :not(:last-child) {
        border-bottom: 1px solid ${t.border.color};
      }
    }
  }
`;

export interface ColumnPanelProps extends StylableComponentProps {
  /** Sets the size of the panel[`xs` - 166,`md` - 200]. */
  size?: 'xs' | 'md';
  /** Children to render (`PanelSection`) */
  children?: ReactNode;
}

const ColumnPanelCompoundParent: FC<React.PropsWithChildren<ColumnPanelProps>> = ({ size = 'md', children, className }) => (
  <Root className={className} size={size}>
    {children}
  </Root>
);

export const ColumnPanel = Object.assign(ColumnPanelCompoundParent, { Section: PanelSection, Record: CheckboxRecord });
